import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Sparkles, X } from 'lucide-react-native';
import { ComponentType, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  Easing,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette } from '@/constants/theme';
import { hapticTap } from '@/lib/haptics';
import {
  getMenuItemByKey,
  parseMenuKeys,
  type QuickActionMenuItem,
} from '@/lib/quick-actions-menu';
import { getActiveProperty } from '@/lib/tasks';

interface Action extends QuickActionMenuItem {
  onPress: () => void;
}

/**
 * High-tech feeling Quick Actions sheet. Triggered from the center FAB on
 * the tab bar. Slides up from the bottom with staggered scale-in on each
 * action button — gives the ADHD-friendly "dopamine hit" of progress.
 */
export function QuickActions({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const db = useSQLiteContext();
  const insets = useSafeAreaInsets();
  const anim = useSharedValue(0);
  // Cache the user-curated key list on the component. Refreshed each time
  // the sheet is opened so changes from /quick-actions show up immediately.
  const [orderKeys, setOrderKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const prop = await getActiveProperty(db);
      setOrderKeys(parseMenuKeys(prop?.quick_actions_keys_json));
    })();
  }, [open, db]);

  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
  }, [open, anim]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
  }));
  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [
      { translateY: (1 - anim.value) * 60 },
      { scale: 0.94 + anim.value * 0.06 },
    ],
  }));

  const go = (path: string) => {
    hapticTap();
    onClose();
    // Tiny delay so the sheet closes visually before the route swaps in.
    setTimeout(() => router.push(path as never), 140);
  };

  // Materialize the user-curated keys into renderable actions. We always
  // show *something* — if persistence hasn't loaded yet, fall back to the
  // defaults so the sheet never appears empty for a beat after opening.
  const keys = orderKeys ?? parseMenuKeys(null);
  const actions: Action[] = keys
    .map((k) => getMenuItemByKey(k))
    .filter((m): m is QuickActionMenuItem => m != null)
    .map((m) => ({ ...m, onPress: () => go(m.route) }));

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
      </Animated.View>

      <View pointerEvents="box-none" style={styles.wrap}>
        {/* Android 15 edge-to-edge: the gesture nav bar draws OVER the
            sheet, hiding Dismiss (Wave 59) — pad by the real inset. */}
        <Animated.View style={[styles.sheet, { paddingBottom: 28 + insets.bottom }, sheetStyle]}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Sparkles size={16} color={Palette.honey} strokeWidth={1.8} />
              <Text style={styles.title}>Quick actions</Text>
            </View>
            <Pressable onPress={onClose} hitSlop={10} style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.grid}>
            {actions.map((a, i) => (
              <ActionTile key={a.key} action={a} index={i} progress={anim} />
            ))}
          </View>

          <Pressable onPress={onClose} style={({ pressed }) => [styles.dismissBtn, pressed && { opacity: 0.7 }]}>
            <Text style={styles.dismissText}>Dismiss</Text>
          </Pressable>
        </Animated.View>
      </View>
    </Modal>
  );
}

function ActionTile({
  action,
  index,
  progress,
}: {
  action: Action;
  index: number;
  progress: SharedValue<number>;
}) {
  const style = useAnimatedStyle(() => {
    // Stagger: each tile starts a tiny bit later than the previous one.
    const offset = Math.min(0.9, index * 0.06);
    const t = Math.max(0, Math.min(1, (progress.value - offset) / (1 - offset)));
    return {
      opacity: t,
      transform: [
        { translateY: (1 - t) * 24 },
        { scale: 0.85 + t * 0.15 },
      ],
    };
  });

  return (
    <Animated.View style={[styles.tileWrap, style]}>
      <Pressable
        onPress={action.onPress}
        accessibilityRole="button"
        accessibilityLabel={action.label}
        style={({ pressed }) => [
          styles.tile,
          pressed && { transform: [{ scale: 0.96 }] },
        ]}>
        <View style={[styles.iconCircle, { backgroundColor: action.color }]}>
          <action.Icon size={22} color={Palette.cream} strokeWidth={1.9} />
        </View>
        <Text style={styles.tileLabel} numberOfLines={1}>{action.label}</Text>
        <Text style={styles.tileSub} numberOfLines={2}>{action.sub}</Text>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 0.55)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 28,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: -6 },
    elevation: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 19,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  tileWrap: {
    width: '31%',
    flexGrow: 1,
    minWidth: 95,
  },
  tile: {
    // flexGrow (NOT flex: 1) equalizes tile heights per row: natural
    // content height sets the row, then shorter tiles grow to match.
    // flex: 1 sets flexBasis 0, and native yoga collapses a basis-0
    // child of an auto-height parent to zero — the sheet rendered
    // empty on Android while web looked fine (Wave 63 hotfix).
    flexGrow: 1,
    backgroundColor: Palette.card,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    padding: 12,
    alignItems: 'center',
    gap: 6,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 2,
  },
  tileLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    textAlign: 'center',
  },
  tileSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 11.5,
    color: Palette.inkSoft,
    textAlign: 'center',
    lineHeight: 13,
  },
  dismissBtn: {
    marginTop: 16,
    alignSelf: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  dismissText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
  },
});
